import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { App, URLOpenListenerEvent } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

const normalizePath = (path: string): string => {
  if (!path) return ''
  return path.startsWith('/') ? path : `/${path}`
}

export const parseAppUrl = (rawUrl: string): string => {
  if (!rawUrl) return ''

  if (rawUrl.includes('app.lvbl.club')) {
    const urlObj = new URL(rawUrl)
    const redirectTo = urlObj.searchParams.get('redirect_to')

    if (redirectTo) {
      return normalizePath(redirectTo)
    }

    if (urlObj.hash) {
      return normalizePath(urlObj.hash.substring(1))
    }

    return normalizePath(urlObj.pathname)
  }

  if (rawUrl.includes('/#/')) {
    return normalizePath(rawUrl.split('/#/').pop() || '')
  }

  const customSchemeRegex = /^[^:]+:\/\/(.*)$/
  const customSchemeMatch = customSchemeRegex.exec(rawUrl)
  if (!customSchemeMatch) return ''

  const customPath = customSchemeMatch[1].replace(/^#/, '')
  return normalizePath(customPath)
}

const AppUrlListener: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Refresh auth state when the app returns to foreground. Do not force a
  // route change here or we can clobber an in-flight universal-link navigation.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const stateListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        queryClient.invalidateQueries('auth')
      }
    })

    return () => {
      stateListener.then((listener) => listener.remove())
    }
  }, [queryClient])

  useEffect(() => {
    // Set up deep link listener
    const deepLinkListener = App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      console.log('Deep link received:', event.url)

      try {
        const path = parseAppUrl(event.url)

        if (path) {
          console.log('Navigating to:', path)
          navigate(path)
        }
      } catch (error) {
        console.error('Error parsing deep link URL:', error)
        // Fallback to original logic
        const slug = event.url.split('/#/').pop()?.replace(/^#/, '')
        if (slug) {
          navigate(normalizePath(slug))
        }
      }
    })

    // Cleanup listeners on unmount
    return () => {
      deepLinkListener.then((listener) => listener.remove())
    }
  }, [navigate])

  return null
}

export default AppUrlListener
